import { useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { SCREEN_HEIGHT_BREAKPOINT } from '@/modules/SCREEN_HEIGHT_BREAKPOINT';

export function useScrollSpy<T extends HTMLElement = HTMLElement>(count: number) {
  const refs = useRef<(T | null)[]>([]);
  const [active, setActive] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const shouldUseOffset =
      window.innerWidth >= theme.breakpoints.values[MAIN_BREAKPOINT] &&
      window.innerHeight >= parseInt(SCREEN_HEIGHT_BREAKPOINT, 10);
    const headerHeight = shouldUseOffset ? 120 : 80;
    const baseOffset = 16;
    const totalOffset = headerHeight + baseOffset;

    const onScroll = () => {
      const tops = refs.current.map((r) => r?.getBoundingClientRect().top ?? 9999);

      const isBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;
      if (isBottom) {
        setActive(count - 1);

        return;
      }

      const idx = tops.findIndex((top) => top > totalOffset);
      setActive(idx === -1 ? count - 1 : Math.max(0, idx - 1));
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [count, theme]);

  const setRef = (i: number) => (el: T | null) => {
    refs.current[i] = el;
  };

  const scrollTo = (i: number) => {
    const element = refs.current[i];
    if (element) {
      const shouldUseOffset =
        window.innerWidth >= theme.breakpoints.values[MAIN_BREAKPOINT] &&
        window.innerHeight >= parseInt(SCREEN_HEIGHT_BREAKPOINT, 10);
      const headerHeight = shouldUseOffset ? 120 : 80;
      const baseOffset = 16;
      const targetPosition = element.offsetTop - headerHeight - baseOffset;

      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: 'smooth',
      });
      setTimeout(() => setActive(i), 100);
    }
  };

  return { setRef, scrollTo, activeIndex: active };
}
